import { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { Anchor, Download, Lock, LogIn, LogOut, ShieldCheck } from "lucide-react";
import { Button } from "./components/ui/button";
import { AuthProvider, useAuth } from "./lib/auth";
import { CREATED_BY } from "./lib/export";
import { LoginDialog } from "./components/LoginDialog";
import { ExportDialog } from "./components/ExportDialog";
import { UsersManager } from "./components/UsersManager";
import { HistoryLog } from "./components/HistoryLog";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { api } from "./lib/api";
import { computeStats } from "./lib/calc";
import { KpiCards } from "./components/KpiCards";
import { TeamStatusList } from "./components/TeamStatusList";
import { PerformanceTable } from "./components/PerformanceTable";
import { AnalystConclusion } from "./components/AnalystConclusion";
import { AssignmentMatrix } from "./components/AssignmentMatrix";
import { DailyLog } from "./components/DailyLog";
import { TeamsEditor } from "./components/TeamsEditor";
import { JobsEditor } from "./components/JobsEditor";
import { SCurveChart } from "./components/SCurveChart";
import { WeatherRecap } from "./components/WeatherRecap";

document.documentElement.classList.add("dark");

const Section = ({ title, children }) => (
  <section className="space-y-4">
    <h2 className="font-display text-lg font-semibold uppercase tracking-wider text-[color:var(--muted)]">{title}</h2>
    {children}
  </section>
);

function Dashboard() {
  const { user, canEdit, isAdmin, logout } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [state, setState] = useState(null);
  const [logs, setLogs] = useState([]);

  const load = useCallback(async () => {
    const [s, l] = await Promise.all([api.get("/state"), api.get("/logs")]);
    setState(s.data);
    setLogs(l.data);
  }, []);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState("overview");
  useEffect(() => { load(); }, [load, user]);

  const run = (fn, msg) => async (...args) => {
    try {
      const r = await fn(...args);
      if (r === false) return;
      await load();
      setRefreshKey((k) => k + 1);
      if (msg) toast.success(msg);
    } catch (e) {
      if (e.response?.status === 401) { toast.error("Please log in as supervisor to make changes"); setLoginOpen(true); return; }
      toast.error(e.response?.data?.detail?.toString() || "Something went wrong");
    }
  };

  const setAssignment = run((team_id, job_id, status) => api.put("/assignments", { team_id, job_id, status }), "Assignment updated");
  const saveLog = run((f, id) => (id ? api.put(`/logs/${id}`, f) : api.post("/logs", f)), "Daily log saved");
  const deleteLog = run((id) => window.confirm("Delete this log?") && api.delete(`/logs/${id}`), "Log deleted");
  const saveTeam = run((f, id) => (id ? api.put(`/teams/${id}`, f) : api.post("/teams", f)), "Team saved");
  const deleteTeam = run((id) => window.confirm("Delete this team and its logs?") && api.delete(`/teams/${id}`), "Team removed");
  const saveJob = run((f, id) => (id ? api.put(`/jobs/${id}`, f) : api.post("/jobs", f)), "Job saved");
  const deleteJob = run((id) => window.confirm("Delete this job and its logs?") && api.delete(`/jobs/${id}`), "Job removed");

  const stats = useMemo(() => (state ? computeStats(state) : null), [state]);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-50 border-b border-[color:var(--border)] bg-[#0b0f19]/80 px-4 py-4 backdrop-blur-md sm:px-8">
        <div className="mx-auto flex max-w-7xl items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-sm bg-amber-500 text-black"><Anchor className="h-5 w-5" /></div>
          <div>
            <h1 className="font-display text-2xl font-bold leading-none tracking-tight sm:text-3xl">Combined Inspection Progress</h1>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-[color:var(--muted)]">Offshore workload tracker</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button data-testid="export-open-button" variant="outline" size="sm" disabled={!stats} onClick={() => setExportOpen(true)} className="border-[color:var(--border)] font-mono text-xs uppercase tracking-wider">
              <Download className="mr-1.5 h-4 w-4" /> Export
            </Button>
            {user ? (
              <>
                <span data-testid="current-user-badge" className="hidden items-center gap-1.5 rounded-sm border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1.5 font-mono text-[11px] text-emerald-300 sm:inline-flex">
                  <ShieldCheck className="h-3.5 w-3.5" /> {user.name} · {user.role}
                </span>
                <Button data-testid="logout-button" variant="ghost" size="sm" onClick={() => { logout(); toast.success("Logged out"); }} className="font-mono text-xs uppercase">
                  <LogOut className="mr-1.5 h-4 w-4" /> Logout
                </Button>
              </>
            ) : (
              <Button data-testid="login-open-button" size="sm" onClick={() => setLoginOpen(true)} className="bg-amber-500 font-mono text-xs uppercase tracking-wider text-black hover:bg-amber-400">
                <LogIn className="mr-1.5 h-4 w-4" /> Supervisor Login
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8">
        {!stats ? (
          <div data-testid="loading" className="font-mono text-sm text-[color:var(--muted)]">Loading...</div>
        ) : (
          <Tabs value={!canEdit && tab === "history" ? "overview" : tab} onValueChange={setTab}>
            <TabsList className="mb-8 h-auto flex-wrap justify-start gap-1 rounded-sm border border-[color:var(--border)] bg-[color:var(--surface)] p-1">
              {[["overview", "Overview"], ["matrix", "Assignment Matrix"], ["daily", "Daily Log"], ["trends", "Trends & Weather"], ["settings", "Teams & Jobs"], ...(canEdit ? [["history", "History"]] : [])].map(([v, l]) => (
                <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-sm px-4 py-2 font-mono text-xs uppercase tracking-wider data-[state=active]:bg-amber-500 data-[state=active]:text-black">{l}</TabsTrigger>
              ))}
            </TabsList>

            {!canEdit && (
              <div data-testid="readonly-banner" className="mb-6 flex items-center gap-2 rounded-sm border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-2.5 text-sm text-[color:var(--muted)]">
                <Lock className="h-4 w-4 text-amber-400" /> View-only mode. <button type="button" data-testid="readonly-login-link" onClick={() => setLoginOpen(true)} className="font-medium text-amber-400 hover:underline">Log in as supervisor</button> to edit.
              </div>
            )}

            <TabsContent value="overview" className="space-y-10">
              <div data-testid="created-by-banner" className="flex items-center justify-between rounded-sm border-l-4 border-l-amber-500 bg-[color:var(--surface)] px-4 py-3">
                <span className="label">Inspection Overview</span>
                <span className="font-mono text-xs uppercase tracking-widest text-[color:var(--muted)]">Created by <span className="font-bold text-amber-400">{CREATED_BY}</span></span>
              </div>
              <KpiCards stats={stats} />
              <Section title="Status per Team"><TeamStatusList perTeam={stats.perTeam} /></Section>
              <Section title="Performance & Workload Analysis"><PerformanceTable perTeam={stats.perTeam} /></Section>
              <AnalystConclusion stats={stats} />
            </TabsContent>

            <TabsContent value="matrix" className="space-y-4">
              <p className="text-sm text-[color:var(--muted)]">{canEdit ? "" : "(View only) "}Assign any job to any team and set its status. Choose "Unassigned" to remove a job from a team.</p>
              <AssignmentMatrix teams={state.teams} jobs={state.jobs} assignments={state.assignments} onChange={setAssignment} readOnly={!canEdit} />
            </TabsContent>

            <TabsContent value="daily">
              <DailyLog logs={logs} teams={state.teams} jobs={state.jobs} onSave={saveLog} onDelete={deleteLog} readOnly={!canEdit} />
            </TabsContent>

            <TabsContent value="trends" className="space-y-10">
              <Section title="S-Curve · Daily Progress"><SCurveChart state={state} logs={logs} /></Section>
              <Section title="Weather Recap · Helideck & Boatlanding"><WeatherRecap jobs={state.jobs} logs={logs} /></Section>
            </TabsContent>

            {canEdit && (
              <TabsContent value="history" className="space-y-4">
                <p className="text-sm text-[color:var(--muted)]">Every change made by supervisors and admins — who did it, when, and what changed.</p>
                <HistoryLog refreshKey={refreshKey} />
              </TabsContent>
            )}

            <TabsContent value="settings" className="grid gap-6 lg:grid-cols-2">
              <TeamsEditor readOnly={!canEdit} teams={state.teams} onSave={saveTeam} onDelete={deleteTeam} />
              <JobsEditor readOnly={!canEdit} jobs={state.jobs} onSave={saveJob} onDelete={deleteJob} />
              {isAdmin && <UsersManager />}
            </TabsContent>
          </Tabs>
        )}
      </main>
      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
      {stats && <ExportDialog open={exportOpen} onOpenChange={setExportOpen} state={state} stats={stats} logs={logs} />}
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

const App = () => (
  <AuthProvider>
    <Dashboard />
  </AuthProvider>
);

export default App;
